export const translations = {
  en: {
    common: {
      actions: {
        cancel: "Cancel",
        save: "Save",
        saveChanges: "Save changes",
        create: "Create",
        delete: "Delete",
        revoke: "Revoke",
        remove: "Remove",
        edit: "edit",
        open: "open",
        copy: "copy",
        copied: "copied",
        close: "Close",
        confirm: "Confirm",
      },
      states: {
        active: "active",
        accepted: "accepted",
        admin: "admin",
        complete: "complete",
        current: "current",
        expired: "expired",
        female: "female",
        incomplete: "incomplete",
        member: "member",
        male: "male",
        owner: "owner",
        pending: "pending",
        revoked: "revoked",
        you: "you",
      },
      fields: {
        account: "Account",
        age: "Age",
        birthDate: "Birth date",
        client: "Client",
        created: "Created",
        device: "Device",
        email: "Email",
        expires: "Expires",
        gender: "Gender",
        gym: "Gym",
        joined: "Joined",
        label: "Label",
        lastUsed: "Last used",
        location: "Location",
        name: "Name",
        password: "Password",
        role: "Role",
        state: "State",
        weight: "Weight (kg)",
      },
      table: {
        actions: "Actions",
        loading: "Loading...",
        noRecords: "No records.",
      },
      misc: {
        language: "Language",
        loading: "Loading...",
        minEightChars: "min 8 chars",
        optional: "optional",
        theme: "Theme",
        themeLight: "Light",
        themeDark: "Dark",
        themeSystem: "System",
      },
      pager: {
        range: "{from}-{to} of {total}",
        prev: "prev",
        next: "next",
        perPage: "{count} / page",
      },
      toasts: {
        copiedPublicLink: "Public intake link copied",
        couldNotCopyPublicLink: "Could not copy the public intake link",
        organizationCreated: "Organization created",
        switchedOrganization: "Switched organization",
        welcomeBack: "Welcome back",
      },
    },
    sidebar: {
      nav: {
        sensors: "Sensors",
        gyms: "Gyms",
        apiKeys: "API keys",
        invites: "Invites",
        members: "Members",
        clients: "Clients",
        help: "Help",
      },
    },
    topbar: {
      openNavigation: "Open navigation",
      organization: "Organization",
      signedInAs: "Signed in as",
      organizations: "Organizations",
      logOut: "Log out",
      errors: {
        couldNotSwitch: "Could not switch organization",
      },
    },
    auth: {
      authenticating: "authenticating...",
      login: {
        sideEyebrow: "Heart-rate administration",
        sideTitle: "Manage gyms, sensors, and RPI integrations.",
        sideDescription:
          "Keep gym organizations, client profiles, and device access organized in one focused admin workspace.",
        eyebrow: "HeartRate",
        title: "Admin login",
        submit: "Sign in",
        submitting: "Signing in...",
        noOrganization: "No organization yet?",
        createOne: "Create one",
        invalidCredentials: "Invalid credentials",
      },
      register: {
        eyebrow: "HeartRate setup",
        title: "Create organization",
        description:
          "Create the gym organization that will own sensors, clients, and RPI integrations.",
        organizationName: "Organization name",
        slug: "Slug",
        slugHint: "/lowercase-with-dashes",
        submit: "Create organization",
        submitting: "Creating...",
        alreadyRegistered: "Already registered?",
        signIn: "Sign in",
        failed: "Registration failed",
      },
      acceptInvite: {
        eyebrow: "Team access",
        title: "Join an organization",
        description:
          "Accept access to help manage gyms, sensors, and RPI device integrations.",
        token: "Invite token",
        submit: "Accept invite",
        submitting: "Joining...",
        helper:
          "This password will create the invited account, or sign in the invited account if it already exists.",
        regularSignIn: "Use regular sign in",
        accepted: "Invite accepted",
        failed: "Could not accept invite",
      },
    },
    sensors: {
      title: "Sensor roster",
      description:
        "Review RPI-registered sensor profiles, assign clients, and keep each gym's heart-rate data ready for training sessions.",
      filters: {
        all: "all",
        complete: "complete",
        incomplete: "incomplete",
      },
      empty:
        "No sensors yet. Devices registering via X-API-Key will appear here.",
      noMatches: "No matches for this filter.",
      columns: {
        sensorId: "sensor id",
        name: "name",
        age: "age",
        state: "state",
        created: "created",
      },
      toast: {
        failedLoad: "Failed to load sensors",
        assigned: "Client assigned",
        createdAndAssigned: "Client created and assigned",
        cleared: "Assignment cleared",
        deleted: "Sensor deleted",
      },
      actions: {
        reassign: "Reassign",
      },
      delete: {
        title: "Delete sensor?",
        description:
          "Permanently remove sensor {sensorId}. This cannot be undone.",
      },
      needsProfile: {
        title: "Needs a profile",
        subtitle: "{count} detected straps without a client",
        assign: "Assign…",
      },
      assign: {
        eyebrow: "ASSIGN SENSOR",
        title: "Assign sensor",
        searchPlaceholder: "Search clients by name or email…",
        createNew: "Create new profile",
        clear: "Clear assignment",
        withoutStrap: "{count} shown without a strap",
        empty: "No clients match your search.",
        noClients: "No clients yet. Create the first profile.",
        loading: "Loading clients…",
        loadingMore: "Loading more…",
        loadError: "Could not load clients",
      },
    },
    gyms: {
      title: "Gyms",
      description:
        "Manage gym locations and keep sensor deployments organized by facility.",
      newGym: "New gym",
      empty: "No gyms yet.",
      noLocation: "No location set",
      columns: {
        name: "name",
        location: "location",
        created: "created",
      },
      toast: {
        updated: "Gym updated",
        created: "Gym created",
        deleted: "Gym deleted",
      },
      dialog: {
        editTitle: "Edit gym",
        createTitle: "New gym",
        description: "Scoped to the current organization.",
      },
      delete: {
        title: "Delete gym?",
        description: "Permanently remove {name}.",
      },
    },
    apiKeys: {
      title: "API keys",
      description:
        "Create and revoke RPI integration keys for gym devices that register heart-rate sensors.",
      newKey: "New key",
      empty: "No keys yet.",
      noGymOrgWide: "No gym (org-wide)",
      plaintextShownOnce: "Plaintext is shown once.",
      columns: {
        name: "name",
        gym: "gym",
        lastUsed: "last used",
        state: "state",
        created: "created",
      },
      toast: {
        revoked: "Key revoked",
      },
      create: {
        title: "New API key",
        scopeToGym: "Scope to gym",
        placeholder: "No gym (org-wide)",
        submit: "Create key",
      },
      secret: {
        title: "API key created",
        description:
          'Key "{name}" is ready. Paste it into your Raspberry-Pi config as X-API-Key.',
      },
      revoke: {
        title: "Revoke API key?",
        description: "Devices using {name} will stop working immediately.",
      },
    },
    invites: {
      title: "Invites",
      description:
        "Invite teammates who help manage gym organizations, sensor profiles, and device integrations.",
      newInvite: "New invite",
      empty: "No invites yet.",
      columns: {
        email: "email",
        role: "role",
        state: "state",
        expires: "expires",
        created: "created",
      },
      toast: {
        emailSent: "Invite email sent to {email}",
        createdFor: "Invite created for {email}",
        revoked: "Invite revoked",
      },
      create: {
        title: "New invite",
        description: "Send an invitation email with a secure join link.",
        submit: "Send invite",
      },
      revoke: {
        title: "Revoke invite?",
        description:
          "{email} will no longer be able to join from the invitation email.",
      },
    },
    members: {
      title: "Members",
      description:
        "Review organization access for the people managing gyms, sensors, and RPI integrations.",
      empty: "No members.",
      columns: {
        email: "email",
        role: "role",
        joined: "joined",
      },
      toast: {
        removed: "Member removed",
      },
      remove: {
        title: "Remove member?",
        description: "{email} will lose access to this organization.",
      },
    },
    clients: {
      title: "Clients",
      description:
        "Maintain client profiles used to complete sensor records and prepare heart-rate training data.",
      newClient: "New client",
      empty: "No clients yet.",
      noLocation: "No location set",
      columns: {
        name: "name",
        email: "email",
        device: "device",
        birthDate: "birth date",
        created: "created",
      },
      publicLink: {
        eyebrow: "Public intake",
        title: "Client intake link",
        description:
          "Anyone with this link can submit a client intake form for your organization.",
        copy: "Copy link",
        open: "Open form",
        loading: "Loading...",
      },
      toast: {
        failedLoad: "Failed to load clients",
        couldNotLoadLink: "Could not load public intake link",
        couldNotLoadDetail: "Could not load client detail",
        updated: "Client updated",
        created: "Client created",
        deleted: "Client deleted",
      },
      dialog: {
        detailTitle: "Client detail",
        createTitle: "New client",
        description:
          "Use the same form to review and update client information.",
        devicePlaceholder: "Select ANT+ device",
        deviceSearch: "Search device...",
        deviceNoResults: "No device found.",
        deviceNotInList: "Not in the list",
        deviceCustomPlaceholder: "Write your device model",
        genderPlaceholder: "No gender selected",
        weightPlaceholder: "Optional weight in kg",
        createSubmit: "Create client",
      },
      delete: {
        title: "Delete client?",
        description: "Permanently remove {name}.",
      },
    },
    help: {
      eyebrow: "/ trainer guide",
      title: "Trainer and admin help",
      description:
        "Use this page when you need the short version: what to set up, what each page does, and how clients should broadcast heart rate.",
      intro: {
        title: "Heart-rate devices must use ANT+",
        description:
          "The system does not read BLE/Bluetooth heart-rate broadcasts right now. Clients need an ANT+ watch, strap, or armband.",
      },
      quickStart: {
        eyebrow: "Setup flow",
        title: "Set up HeartStream in three steps",
        description:
          "This is the normal order for a new gym or a new training group.",
        items: [
          {
            title: "Create gyms",
            description: "Add the locations where training happens.",
          },
          {
            title: "Add clients",
            description:
              "Create client profiles yourself or send the public intake link.",
          },
          {
            title: "Assign sensors",
            description:
              "When sensors appear, connect each one to the right client.",
          },
        ],
      },
      workflowEyebrow: "Pages",
      workflowTitle: "What each page is for",
      workflowDescription:
        "Use this section when you are not sure where to do a task.",
      workflows: [
        {
          key: "gyms",
          icon: "gyms",
          title: "Gyms",
          description: "Manage training locations.",
          to: "/gyms",
          action: "Open Gyms",
          steps: [
            "Create one record for each gym or studio.",
            "Use names that trainers can recognize quickly.",
          ],
        },
        {
          key: "apiKeys",
          icon: "apiKeys",
          title: "API keys",
          description: "Let Raspberry Pi devices connect.",
          to: "/keys",
          action: "Open API keys",
          steps: [
            "Use a separate key for each device or gym.",
            "API keys are for the person configuring devices, not for day-to-day trainer work.",
          ],
        },
        {
          key: "sensors",
          icon: "sensors",
          title: "Sensors",
          description: "Match incoming heart-rate sensors to clients.",
          to: "/sensors",
          action: "Open Sensors",
          steps: [
            "Sensors appear automatically after the first heart-rate reading.",
            "Open incomplete sensors, assign clients, check the details, and save.",
          ],
        },
        {
          key: "clients",
          icon: "clients",
          title: "Clients",
          description: "Keep client details ready for training.",
          to: "/clients",
          action: "Open Clients",
          steps: [
            "Store name, email, and birth date.",
            "Share the intake link when clients should enter their own details.",
            "The system uses birth date to calculate heart-rate zones.",
          ],
        },
        {
          key: "invites",
          icon: "invites",
          title: "Invites",
          description: "Invite people who need admin access.",
          to: "/invites",
          action: "Open Invites",
          steps: [
            "Use admin for normal management.",
            "Use owner only for people who should manage access.",
          ],
        },
        {
          key: "members",
          icon: "members",
          title: "Members",
          description: "Review current organization access.",
          to: "/members",
          action: "Open Members",
          steps: [
            "Check who has joined.",
            "Owners can remove people who should no longer have access.",
          ],
        },
      ],
      broadcast: {
        eyebrow: "ANT+ heart rate",
        title: "How clients should turn on heart-rate broadcast",
        description:
          "Use ANT+ only. To check a watch or sensor, open the official ANT+ Product Directory and confirm it supports the Heart Rate profile.",
        checkCompatibility: "Check ANT+ compatibility",
        devices: [
          {
            name: "Garmin watch",
            connection: "ANT+",
            steps: [
              "Open heart-rate settings or controls.",
              "Choose Broadcast Heart Rate.",
              "Pair it from the receiving device.",
            ],
            note: "Stop broadcasting after the session to save battery.",
          },
          {
            name: "Polar sensor",
            connection: "ANT+",
            steps: [
              "Use Polar H9, H10, OH1, or Verity Sense.",
              "Put the sensor on and wake it up.",
              "Pair it from the receiving device.",
            ],
            note: "Polar watches are not recommended for this setup unless you know the exact model broadcasts ANT+.",
          },
          {
            name: "COROS watch",
            connection: "ANT+",
            steps: [
              "Hold BACK from the main watch face to open Toolbox.",
              "Choose Broadcast HR.",
              "Pair it from the receiving device.",
            ],
            note: "Keep the watch in Broadcast HR mode during the session.",
          },
          {
            name: "Other heart-rate straps",
            connection: "ANT+",
            steps: [
              "Confirm the strap supports ANT+.",
              "Put it on, wake it up, and pair it from the receiving device.",
            ],
            note: "Straps without ANT+ will not work.",
          },
        ],
      },
      faq: {
        eyebrow: "Start here",
        title: "Common trainer questions",
        description:
          "These answers cover the most important rules before a session starts.",
        items: [
          {
            question: "Can clients use BLE or Bluetooth heart-rate broadcast?",
            answer:
              "No. The current system supports ANT+ heart-rate broadcast only.",
          },
          {
            question: "How do I check whether a watch or sensor is supported?",
            answer:
              "Open the official ANT+ Product Directory, search for the device, and confirm it supports the Heart Rate profile.",
          },
          {
            question: "Why is an API key needed?",
            answer:
              "An API key lets a Raspberry Pi device connect to the organization. Trainers usually do not need to use API keys themselves.",
          },
          {
            question: "Do I create sensors manually?",
            answer:
              "No. Sensors appear automatically after the receiving device sees a client's heart-rate broadcast. Then you assign the sensor to the client.",
          },
          {
            question: "Why can the device not find a client's heart rate?",
            answer:
              "Check four things: the watch or sensor supports ANT+, broadcast mode is on, the sensor is awake, and the battery is charged.",
          },
          {
            question: "What is the public intake link for?",
            answer:
              "Clients can submit their own name, email, birth date, and consent. The system uses those details to prepare their profile and calculate heart-rate zones.",
          },
        ],
      },
    },
    publicIntake: {
      fallbackTitle: "nášho fitka",
      title: "Ahoj z {organizationName}",
      intro1: "Inovácie poháňajú svet, preto chceme inovovať aj naše fitko.",
      intro2:
        "Testujeme novú aplikáciu, ktorá bude v reálnom čase zobrazovať tep a tepovú zónu na televízii. Tréner vďaka tomu uvidí, ako telo reaguje, a bude vedieť nastaviť efektívnejší tréning. Vyplň prosím tieto údaje, aby sme ťa vedeli správne priradiť.",
      fullName: "Meno a priezvisko",
      email: "E-mail",
      birthDate: "Dátum narodenia",
      device: "Zariadenie",
      devicePlaceholder: "Vyber zariadenie s ANT+",
      deviceSearch: "Vyhladaj zariadenie...",
      deviceNoResults: "Zariadenie sa nenaslo.",
      deviceNotInList: "Nie je v zozname",
      deviceCustomPlaceholder: "Napis model zariadenia",
      consent:
        "Súhlasím so spracúvaním môjho mena, e-mailu, dátumu narodenia a súvisiacich tréningových údajov na účely tréningu, výpočtu tepových zón a práce trénera v súlade s",
      termsLink: "podmienkami a informáciami o spracúvaní údajov",
      consentSuffix: ".",
      submit: "Odoslať údaje",
      submitting: "Odosiela sa...",
      successTitle: "Ďakujeme, máme všetko.",
      success: "Tešíme sa na teba na tréningu.",
      toastConsent:
        "Pred odoslaním prosím potvrď súhlas so spracúvaním údajov.",
      toastDevice: "Vyber prosim zariadenie alebo zadaj vlastny model.",
      toastSubmitted: "Formulár bol odoslaný",
      loadError: "Formulár sa nepodarilo načítať",
      missingToken: "Chýba token formulára",
      submitError: "Formulár sa nepodarilo odoslať",
    },
    terms: {
      eyebrow: "/ legal",
      title: "Terms and data processing information",
      intro:
        "This notice explains how the training organization that sent you the intake link and the HeartStream platform process the personal data you submit through the intake form.",
      sections: [
        {
          title: "1. Who is responsible for your data",
          paragraphs: [
            "The trainer, gym, or organization that sent you this intake link is the party that decides why your data is collected and how it will be used for training and coaching. For that reason, that organization is the primary controller of the personal data you submit through the form.",
            "HeartStream provides the software used to collect and store the submitted data on behalf of that organization. In that role, HeartStream operates as a service provider and may process the data under the instructions of the organization using the platform.",
          ],
        },
        {
          title: "2. What data is collected",
          paragraphs: [
            "The intake form currently collects the following categories of personal data:",
            "The organization may also later connect this profile to training-related data, including heart rate data broadcast from your device during exercise sessions.",
          ],
          list: [
            "your full name,",
            "your email address,",
            "your birth date,",
            "your consent confirmation, including the date and version of the consent text.",
          ],
        },
        {
          title: "3. Why the data is used",
          paragraphs: [
            "Your trainer sends you this intake link so the organization can prepare your profile for heart rate zone calculation and training analysis based on the heart rate you broadcast during training.",
            "Your submitted data may therefore be used for the following purposes:",
          ],
          list: [
            "identifying you correctly in the training system,",
            "setting up and maintaining your athlete or client profile,",
            "supporting heart rate zone calculation and related coaching decisions,",
            "operating the training service and keeping related records.",
          ],
        },
        {
          title: "4. Legal basis and consent",
          paragraphs: [
            "By ticking the consent checkbox and submitting the form, you confirm that you understand how the submitted data will be used and that you agree to its processing for the purposes described in this notice.",
            "The system stores the time at which consent was given together with the version of the consent text in force when you submitted the form. If the organization relies on your consent as the legal basis for processing, you may withdraw that consent later by contacting the trainer or organization that sent you the link.",
            "Withdrawal of consent does not affect processing that took place before the withdrawal request was made.",
          ],
        },
        {
          title: "5. Who may receive your data",
          paragraphs: [
            "Your data may be accessed by:",
            "Your data should not be used for unrelated marketing or unrelated commercial purposes unless a separate lawful basis exists and you are informed accordingly.",
          ],
          list: [
            "the trainer or organization staff responsible for your training profile,",
            "HeartStream as the software provider operating the platform for that organization,",
            "technical service providers involved in hosting, storage, and system security where needed.",
          ],
        },
        {
          title: "6. How long the data is kept",
          paragraphs: [
            "The submitted data should be kept only for as long as necessary for training administration, coaching, legal compliance, and record-keeping needs of the organization operating the form.",
            "If you want to know the specific retention period that applies to your profile, please contact the trainer or organization that sent you the intake link.",
          ],
        },
        {
          title: "7. Your rights",
          paragraphs: [
            "Depending on the applicable law and the role of the organization handling your data, you may have the right to request access, correction, deletion, restriction of processing, objection, or withdrawal of consent.",
            "Requests concerning your submitted data should be directed to the trainer or organization that sent you this intake link, because that organization is the main point of contact for decisions about your data.",
          ],
        },
        {
          title: "8. Security and complaints",
          paragraphs: [
            "The platform is intended to use reasonable technical and organizational measures to protect the submitted data against unauthorized access, loss, misuse, or unlawful disclosure.",
            "If you believe your data has been processed unlawfully, you may contact the organization that sent you the link and, where applicable, file a complaint with the competent personal data protection authority.",
          ],
        },
      ],
    },
    secretDialog: {
      warning:
        "This value is shown only once. Copy and store it somewhere safe; closing this dialog removes access forever.",
      acknowledge: "I've saved this value somewhere safe.",
    },
  },
  sk: {
    common: {
      actions: {
        cancel: "Zrušiť",
        save: "Uložiť",
        saveChanges: "Uložiť zmeny",
        create: "Vytvoriť",
        delete: "Odstrániť",
        revoke: "Zneplatniť",
        remove: "Odobrať",
        edit: "upraviť",
        open: "otvoriť",
        copy: "kopírovať",
        copied: "skopírované",
        close: "Zavrieť",
        confirm: "Potvrdiť",
      },
      states: {
        active: "aktívny",
        accepted: "prijaté",
        admin: "admin",
        complete: "kompletné",
        current: "aktuálne",
        expired: "po platnosti",
        female: "žena",
        male: "muž",
        incomplete: "nekompletné",
        member: "člen",
        owner: "vlastník",
        pending: "čaká na prijatie",
        revoked: "zneplatnené",
        you: "vy",
      },
      fields: {
        account: "Účet",
        age: "Vek",
        birthDate: "Dátum narodenia",
        client: "Klient",
        created: "Vytvorené",
        device: "Zariadenie",
        email: "E-mail",
        expires: "Platí do",
        gym: "Gym",
        joined: "Pridané",
        label: "Označenie",
        lastUsed: "Naposledy použité",
        location: "Lokalita",
        name: "Meno",
        password: "Heslo",
        role: "Rola",
        state: "Stav",
        weight: "Hmotnosť (kg)",
        gender: "Pohlavie",
      },
      table: {
        actions: "Akcie",
        loading: "Načítava sa...",
        noRecords: "Žiadne záznamy.",
      },
      misc: {
        language: "Jazyk",
        loading: "Načítava sa...",
        minEightChars: "min. 8 znakov",
        optional: "voliteľné",
        theme: "Téma",
        themeLight: "Svetlá",
        themeDark: "Tmavá",
        themeSystem: "Systém",
      },
      pager: {
        range: "{from}-{to} z {total}",
        prev: "predch.",
        next: "ďalej",
        perPage: "{count} / strana",
      },
      toasts: {
        copiedPublicLink: "Odkaz na verejný formulár bol skopírovaný",
        couldNotCopyPublicLink:
          "Verejný odkaz na formulár sa nepodarilo skopírovať",
        organizationCreated: "Organizácia bola vytvorená",
        switchedOrganization: "Organizácia bola zmenená",
        welcomeBack: "Vitajte späť",
      },
    },
    sidebar: {
      nav: {
        sensors: "Senzory",
        gyms: "Gymy",
        apiKeys: "API kľúče",
        invites: "Pozvánky",
        members: "Členovia",
        clients: "Klienti",
        help: "Pomoc",
      },
    },
    topbar: {
      openNavigation: "Otvoriť navigáciu",
      organization: "Organizácia",
      signedInAs: "Prihlásený ako",
      organizations: "Organizácie",
      logOut: "Odhlásiť sa",
      errors: {
        couldNotSwitch: "Organizáciu sa nepodarilo zmeniť",
      },
    },
    auth: {
      authenticating: "overujeme prihlásenie...",
      login: {
        sideEyebrow: "Administrácia tepovej frekvencie",
        sideTitle: "Gymy, senzory a RPI integrácie na jednom mieste.",
        sideDescription:
          "Majte organizácie gymov, klientské profily a prístupy zariadení pod kontrolou v prehľadnom administračnom prostredí.",
        eyebrow: "HeartRate",
        title: "Prihlásenie do administrácie",
        submit: "Prihlásiť sa",
        submitting: "Prihlasuje sa...",
        noOrganization: "Ešte nemáte vytvorenú organizáciu?",
        createOne: "Vytvoriť organizáciu",
        invalidCredentials: "Neplatné prihlasovacie údaje",
      },
      register: {
        eyebrow: "Nastavenie HeartRate",
        title: "Vytvoriť organizáciu",
        description:
          "Vytvorte organizáciu, pod ktorou budete spravovať senzory, klientov a RPI integrácie.",
        organizationName: "Názov organizácie",
        slug: "Slug",
        slugHint: "/male-pismena-s-pomlckami",
        submit: "Vytvoriť organizáciu",
        submitting: "Vytvára sa...",
        alreadyRegistered: "Už máte účet?",
        signIn: "Prihlásiť sa",
        failed: "Registrácia zlyhala",
      },
      acceptInvite: {
        eyebrow: "Tímový prístup",
        title: "Pripojiť sa k organizácii",
        description:
          "Prijmite pozvánku a získajte prístup k správe gymov, senzorov a RPI integrácií.",
        token: "Token pozvánky",
        submit: "Prijať pozvánku",
        submitting: "Pripája sa...",
        helper:
          "Týmto heslom vytvoríte pozvaný účet. Ak už účet existuje, použije sa na prihlásenie.",
        regularSignIn: "Použiť bežné prihlásenie",
        accepted: "Pozvánka bola prijatá",
        failed: "Pozvánku sa nepodarilo prijať",
      },
    },
    sensors: {
      title: "Zoznam senzorov",
      description:
        "Kontrolujte senzory registrované cez RPI, priraďujte ich ku klientom a udržujte údaje o tepe pripravené na tréning.",
      filters: {
        all: "všetky",
        complete: "kompletné",
        incomplete: "nekompletné",
      },
      empty:
        "Zatiaľ žiadne senzory. Zariadenia registrované cez X-API-Key sa zobrazia tu.",
      noMatches: "Tomuto filtru nezodpovedá žiadny senzor.",
      columns: {
        sensorId: "id senzora",
        name: "meno",
        age: "vek",
        state: "stav",
        created: "vytvorené",
      },
      toast: {
        failedLoad: "Senzory sa nepodarilo načítať",
        assigned: "Klient bol priradený",
        createdAndAssigned: "Klient bol vytvorený a priradený",
        cleared: "Priradenie bolo zrušené",
        deleted: "Senzor bol odstránený",
      },
      actions: {
        reassign: "Zmeniť priradenie",
      },
      delete: {
        title: "Odstrániť senzor?",
        description:
          "Senzor {sensorId} bude natrvalo odstránený. Túto akciu nie je možné vrátiť späť.",
      },
      needsProfile: {
        title: "Vyžaduje profil",
        subtitle: "{count} detegovaných pásov bez klienta",
        assign: "Priradiť…",
      },
      assign: {
        eyebrow: "PRIRADIŤ SENZOR",
        title: "Priradiť senzor",
        searchPlaceholder: "Hľadať klientov podľa mena alebo emailu…",
        createNew: "Vytvoriť nový profil",
        clear: "Zrušiť priradenie",
        withoutStrap: "{count} zobrazených bez pásu",
        empty: "Vyhľadávaniu nezodpovedá žiadny klient.",
        noClients: "Zatiaľ žiadni klienti. Vytvorte prvý profil.",
        loading: "Načítavajú sa klienti…",
        loadingMore: "Načítavam ďalších…",
        loadError: "Klientov sa nepodarilo načítať",
      },
    },
    gyms: {
      title: "Gymy",
      description:
        "Spravujte lokality gymov a udržujte nasadenie senzorov prehľadne podľa prevádzok.",
      newGym: "Nový gym",
      empty: "Zatiaľ žiadne gymy.",
      noLocation: "Lokalita nie je nastavená",
      columns: {
        name: "názov",
        location: "lokalita",
        created: "vytvorené",
      },
      toast: {
        updated: "Gym bol aktualizovaný",
        created: "Gym bol vytvorený",
        deleted: "Gym bol odstránený",
      },
      dialog: {
        editTitle: "Upraviť gym",
        createTitle: "Nový gym",
        description: "Platí pre aktuálnu organizáciu.",
      },
      delete: {
        title: "Odstrániť gym?",
        description: "Gym {name} bude natrvalo odstránený.",
      },
    },
    apiKeys: {
      title: "API kľúče",
      description:
        "Vytvárajte a zneplatňujte RPI integračné kľúče pre zariadenia, ktoré v gymoch registrujú senzory tepovej frekvencie.",
      newKey: "Nový kľúč",
      empty: "Zatiaľ žiadne kľúče.",
      noGymOrgWide: "Bez priradeného gymu (celá organizácia)",
      plaintextShownOnce: "Tajná hodnota sa zobrazí iba raz.",
      columns: {
        name: "názov",
        gym: "gym",
        lastUsed: "naposledy použité",
        state: "stav",
        created: "vytvorené",
      },
      toast: {
        revoked: "Kľúč bol zneplatnený",
      },
      create: {
        title: "Nový API kľúč",
        scopeToGym: "Obmedziť na gym",
        placeholder: "Bez priradeného gymu",
        submit: "Vytvoriť kľúč",
      },
      secret: {
        title: "API kľúč bol vytvorený",
        description:
          'Kľúč "{name}" je pripravený. Vložte ho do konfigurácie Raspberry-Pi ako X-API-Key.',
      },
      revoke: {
        title: "Zneplatniť API kľúč?",
        description:
          "Zariadenia používajúce kľúč {name} prestanú okamžite fungovať.",
      },
    },
    invites: {
      title: "Pozvánky",
      description:
        "Pozývajte kolegov, ktorí pomáhajú spravovať gymy, profily senzorov a integrácie zariadení.",
      newInvite: "Nová pozvánka",
      empty: "Zatiaľ žiadne pozvánky.",
      columns: {
        email: "e-mail",
        role: "rola",
        state: "stav",
        expires: "platí do",
        created: "vytvorené",
      },
      toast: {
        emailSent: "Pozvánka bola odoslaná na {email}",
        createdFor: "Pozvánka pre {email} bola vytvorená",
        revoked: "Pozvánka bola zneplatnená",
      },
      create: {
        title: "Nová pozvánka",
        description: "Odošlite e-mail so zabezpečeným odkazom na pripojenie.",
        submit: "Odoslať pozvánku",
      },
      revoke: {
        title: "Zneplatniť pozvánku?",
        description: "{email} sa už nebude môcť pripojiť cez túto pozvánku.",
      },
    },
    members: {
      title: "Členovia",
      description:
        "Majte prehľad o ľuďoch, ktorí majú prístup k správe gymov, senzorov a RPI integrácií.",
      empty: "Žiadni členovia.",
      columns: {
        email: "e-mail",
        role: "rola",
        joined: "pridané",
      },
      toast: {
        removed: "Člen bol odobratý",
      },
      remove: {
        title: "Odobrať člena?",
        description: "{email} stratí prístup k tejto organizácii.",
      },
    },
    clients: {
      title: "Klienti",
      description:
        "Spravujte klientské profily, ktoré dopĺňajú záznamy senzorov a pripravujú údaje o tepe na tréning.",
      newClient: "Nový klient",
      empty: "Zatiaľ žiadni klienti.",
      noLocation: "Lokalita nie je vyplnená",
      columns: {
        name: "meno",
        email: "e-mail",
        device: "zariadenie",
        birthDate: "dátum narodenia",
        created: "vytvorené",
      },
      publicLink: {
        eyebrow: "Verejný formulár",
        title: "Odkaz na formulár pre klienta",
        description:
          "Klient s týmto odkazom môže odoslať vstupné údaje priamo do vašej organizácie.",
        copy: "Kopírovať odkaz",
        open: "Otvoriť formulár",
        loading: "Načítava sa...",
      },
      toast: {
        failedLoad: "Zoznam klientov sa nepodarilo načítať",
        couldNotLoadLink: "Odkaz na verejný formulár sa nepodarilo načítať",
        couldNotLoadDetail: "Detail klienta sa nepodarilo načítať",
        updated: "Klient bol aktualizovaný",
        created: "Klient bol vytvorený",
        deleted: "Klient bol odstránený",
      },
      dialog: {
        detailTitle: "Detail klienta",
        createTitle: "Nový klient",
        description: "Skontrolujte a upravte základné údaje klienta.",
        devicePlaceholder: "Vyberte ANT+ zariadenie",
        deviceSearch: "Hľadať zariadenie...",
        deviceNoResults: "Zariadenie sa nenašlo.",
        deviceNotInList: "Nie je v zozname",
        genderPlaceholder: "Pohlavie nie je vybrané",
        weightPlaceholder: "Voliteľná hmotnosť v kg",
        deviceCustomPlaceholder: "Napíšte model zariadenia",
        createSubmit: "Vytvoriť klienta",
      },
      delete: {
        title: "Odstrániť klienta?",
        description: "Klient {name} bude natrvalo odstránený.",
      },
    },
    help: {
      eyebrow: "/ príručka trénera",
      title: "Pomoc pre trénerov a administrátorov",
      description:
        "Krátky návod: čo nastaviť, na čo slúžia jednotlivé stránky a ako majú klienti vysielať tep.",
      intro: {
        title: "Zariadenia na tep musia používať ANT+",
        description:
          "Systém teraz nečíta BLE/Bluetooth vysielanie tepu. Klienti potrebujú ANT+ hodinky, hrudný pás alebo ramenný senzor.",
      },
      quickStart: {
        eyebrow: "Postup nastavenia",
        title: "HeartStream nastavte v troch krokoch",
        description:
          "Toto je bežný postup pre nový gym alebo novú tréningovú skupinu.",
        items: [
          {
            title: "Vytvorte gymy",
            description: "Pridajte miesta, kde prebiehajú tréningy.",
          },
          {
            title: "Pridajte klientov",
            description:
              "Vytvorte profily ručne alebo pošlite klientom verejný formulár.",
          },
          {
            title: "Priraďte senzory",
            description:
              "Keď sa senzory zobrazia, priraďte každý senzor ku správnemu klientovi.",
          },
        ],
      },
      workflowEyebrow: "Stránky",
      workflowTitle: "Na čo slúži každá stránka",
      workflowDescription:
        "Túto časť použite, keď neviete, kde urobiť konkrétnu úlohu.",
      workflows: [
        {
          key: "gyms",
          icon: "gyms",
          title: "Gymy",
          description: "Správa tréningových miest.",
          to: "/gyms",
          action: "Otvoriť Gymy",
          steps: [
            "Vytvorte jeden záznam pre každý gym alebo štúdio.",
            "Používajte názvy, ktoré tréner rýchlo spozná.",
          ],
        },
        {
          key: "apiKeys",
          icon: "apiKeys",
          title: "API kľúče",
          description: "Pripájajú Raspberry Pi zariadenia.",
          to: "/keys",
          action: "Otvoriť API kľúče",
          steps: [
            "Použite samostatný kľúč pre každé zariadenie alebo gym.",
            "API kľúče sú pre osobu, ktorá nastavuje zariadenia, nie na bežnú prácu trénera.",
          ],
        },
        {
          key: "sensors",
          icon: "sensors",
          title: "Senzory",
          description: "Priradenie prichádzajúcich senzorov tepu ku klientom.",
          to: "/sensors",
          action: "Otvoriť Senzory",
          steps: [
            "Senzory sa zobrazia automaticky po prvom načítaní tepu.",
            "Otvorte nekompletné senzory, priraďte klientov, skontrolujte údaje a uložte.",
          ],
        },
        {
          key: "clients",
          icon: "clients",
          title: "Klienti",
          description: "Údaje klientov pripravené na tréning.",
          to: "/clients",
          action: "Otvoriť Klientov",
          steps: [
            "Uložte meno, e-mail a dátum narodenia.",
            "Verejný formulár použite, keď majú klienti doplniť údaje sami.",
            "Dátum narodenia systém používa na výpočet tepových zón.",
          ],
        },
        {
          key: "invites",
          icon: "invites",
          title: "Pozvánky",
          description:
            "Pozvanie ľudí, ktorí potrebujú administrátorský prístup.",
          to: "/invites",
          action: "Otvoriť Pozvánky",
          steps: [
            "Rolu admin použite na bežnú správu organizácie.",
            "Rolu vlastník použite iba pre ľudí, ktorí majú spravovať prístupy.",
          ],
        },
        {
          key: "members",
          icon: "members",
          title: "Členovia",
          description: "Kontrola aktuálneho prístupu do organizácie.",
          to: "/members",
          action: "Otvoriť Členov",
          steps: [
            "Skontrolujte, kto sa už pripojil.",
            "Vlastníci môžu odstrániť ľudí, ktorí už nemajú mať prístup.",
          ],
        },
      ],
      broadcast: {
        eyebrow: "ANT+ tep",
        title: "Ako majú klienti zapnúť vysielanie tepu",
        description:
          "Používajte iba ANT+. Ak chcete skontrolovať hodinky alebo senzor, otvorte oficiálny ANT+ Product Directory a overte podporu profilu Heart Rate.",
        checkCompatibility: "Skontrolovať ANT+ kompatibilitu",
        devices: [
          {
            name: "Garmin hodinky",
            connection: "ANT+",
            steps: [
              "Otvorte nastavenia tepu alebo ovládacie prvky.",
              "Vyberte Broadcast Heart Rate.",
              "Spárujte hodinky z prijímacieho zariadenia.",
            ],
            note: "Po tréningu vysielanie zastavte, aby sa šetrila batéria.",
          },
          {
            name: "Polar senzor",
            connection: "ANT+",
            steps: [
              "Použite Polar H9, H10, OH1 alebo Verity Sense.",
              "Nasaďte senzor a aktivujte ho.",
              "Spárujte senzor z prijímacieho zariadenia.",
            ],
            note: "Polar hodinky neodporúčame, ak presne neviete, že daný model vysiela cez ANT+.",
          },
          {
            name: "COROS hodinky",
            connection: "ANT+",
            steps: [
              "Na hlavnom ciferníku podržte BACK a otvorte Toolbox.",
              "Vyberte Broadcast HR.",
              "Spárujte hodinky z prijímacieho zariadenia.",
            ],
            note: "Počas tréningu nechajte hodinky v režime Broadcast HR.",
          },
          {
            name: "Iné senzory tepu",
            connection: "ANT+",
            steps: [
              "Overte, že senzor podporuje ANT+.",
              "Nasaďte ho, aktivujte a spárujte z prijímacieho zariadenia.",
            ],
            note: "Senzory bez ANT+ nebudú fungovať.",
          },
        ],
      },
      faq: {
        eyebrow: "Začnite tu",
        title: "Najčastejšie otázky trénerov",
        description:
          "Tieto odpovede pokrývajú najdôležitejšie pravidlá pred začiatkom tréningu.",
        items: [
          {
            question:
              "Môžu klienti používať BLE alebo Bluetooth vysielanie tepu?",
            answer: "Nie. Aktuálny systém podporuje iba ANT+ vysielanie tepu.",
          },
          {
            question:
              "Ako skontrolujem, či sú hodinky alebo senzor podporované?",
            answer:
              "Otvorte oficiálny ANT+ Product Directory, vyhľadajte zariadenie a overte podporu profilu Heart Rate.",
          },
          {
            question: "Prečo je potrebný API kľúč?",
            answer:
              "API kľúč umožní Raspberry Pi zariadeniu pripojiť sa k organizácii. Tréneri ho zvyčajne pri bežnej práci nepoužívajú.",
          },
          {
            question: "Vytváram senzory ručne?",
            answer:
              "Nie. Senzory sa zobrazia automaticky, keď prijímacie zariadenie zachytí vysielanie tepu klienta. Potom senzor priradíte ku klientovi.",
          },
          {
            question: "Prečo zariadenie nevie nájsť tep klienta?",
            answer:
              "Skontrolujte štyri veci: hodinky alebo senzor podporujú ANT+, vysielanie je zapnuté, senzor je aktívny a batéria je nabitá.",
          },
          {
            question: "Na čo slúži verejný formulár?",
            answer:
              "Klienti môžu sami odoslať meno, e-mail, dátum narodenia a súhlas. Systém tieto údaje použije na prípravu profilu a výpočet tepových zón.",
          },
        ],
      },
    },
    publicIntake: {
      fallbackTitle: "nášho fitka",
      title: "Ahoj z {organizationName}",
      intro1: "Inovácie poháňajú svet, preto chceme inovovať aj naše fitko.",
      intro2:
        "Testujeme novú aplikáciu, ktorá bude v reálnom čase zobrazovať tep a tepovú zónu na televízii. Tréner vďaka tomu uvidí, ako telo reaguje, a bude vedieť nastaviť efektívnejší tréning. Vyplň prosím tieto údaje, aby sme ťa vedeli správne priradiť.",
      fullName: "Celé meno",
      email: "E-mail",
      birthDate: "Dátum narodenia",
      device: "Zariadenie, ktoré pouzívaš",
      devicePlaceholder: "Vyber zariadenie s ANT+",
      deviceSearch: "Vyhľadaj zariadenie...",
      deviceNoResults: "Zariadenie sa nenašlo.",
      deviceNotInList: "Nie je v zozname",
      deviceCustomPlaceholder: "Napíš model zariadenia",
      consent:
        "Súhlasím so spracúvaním môjho mena, e-mailu, dátumu narodenia a súvisiacich tréningových údajov na účely tréningu, výpočtu tepových zón a práce trénera v súlade s",
      termsLink: "podmienkami a informáciami o spracúvaní údajov",
      consentSuffix: ".",
      submit: "Odoslať údaje",
      submitting: "Odosiela sa...",
      successTitle: "Ďakujeme, máme všetko.",
      success: "Tešíme sa na teba na tréningu.",
      toastConsent:
        "Pred odoslaním prosím potvrď súhlas so spracúvaním údajov.",
      toastDevice: "Vyber prosím zariadenie alebo zadaj vlastný model.",
      toastSubmitted: "Formulár bol odoslaný",
      loadError: "Formulár sa nepodarilo načítať",
      missingToken: "Chýba token formulára",
      submitError: "Formulár sa nepodarilo odoslať",
    },
    terms: {
      eyebrow: "/ právne",
      title: "Podmienky a informácie o spracúvaní údajov",
      intro:
        "Toto oznámenie vysvetľuje, ako tréningová organizácia, ktorá vám poslala odkaz na formulár, a platforma HeartStream spracúvajú osobné údaje odoslané cez tento formulár.",
      sections: [
        {
          title: "1. Kto zodpovedá za vaše údaje",
          paragraphs: [
            "Tréner, gym alebo organizácia, ktorá vám poslala tento odkaz, rozhoduje o tom, prečo sa vaše údaje zbierajú a ako sa použijú pri tréningu a koučingu. Táto organizácia je preto hlavným prevádzkovateľom osobných údajov odoslaných cez formulár.",
            "HeartStream poskytuje softvér na zber a uloženie údajov v mene tejto organizácie. V tejto úlohe vystupuje ako poskytovateľ služby a údaje spracúva podľa pokynov organizácie používajúcej platformu.",
          ],
        },
        {
          title: "2. Aké údaje sa zbierajú",
          paragraphs: [
            "Formulár v súčasnosti zbiera tieto kategórie osobných údajov:",
            "Organizácia môže tento profil neskôr prepojiť aj s tréningovými údajmi vrátane údajov o tepovej frekvencii vysielaných z vášho zariadenia počas cvičenia.",
          ],
          list: [
            "vaše celé meno,",
            "vašu e-mailovú adresu,",
            "váš dátum narodenia,",
            "potvrdenie súhlasu vrátane dátumu a verzie textu súhlasu.",
          ],
        },
        {
          title: "3. Prečo sa údaje používajú",
          paragraphs: [
            "Tréner vám posiela tento odkaz, aby organizácia mohla pripraviť váš profil na výpočet zón tepovej frekvencie a tréningovú analýzu podľa tepu zaznamenaného počas tréningu.",
            "Odoslané údaje sa preto môžu použiť na tieto účely:",
          ],
          list: [
            "správna identifikácia v tréningovom systéme,",
            "nastavenie a správa profilu športovca alebo klienta,",
            "výpočet zón tepovej frekvencie a podpora súvisiacich koučovacích rozhodnutí,",
            "prevádzka tréningovej služby a vedenie súvisiacich záznamov.",
          ],
        },
        {
          title: "4. Právny základ a súhlas",
          paragraphs: [
            "Zaškrtnutím políčka súhlasu a odoslaním formulára potvrdzujete, že rozumiete použitiu odoslaných údajov a súhlasíte s ich spracúvaním na účely opísané v tomto oznámení.",
            "Systém uchováva čas udelenia súhlasu spolu s verziou textu súhlasu platnou pri odoslaní formulára. Ak organizácia používa váš súhlas ako právny základ spracúvania, môžete ho neskôr odvolať kontaktovaním trénera alebo organizácie, ktorá vám poslala odkaz.",
            "Odvolanie súhlasu nemá vplyv na spracúvanie, ktoré prebehlo pred žiadosťou o odvolanie.",
          ],
        },
        {
          title: "5. Kto môže získať prístup k údajom",
          paragraphs: [
            "K vašim údajom môžu mať prístup:",
            "Vaše údaje by sa nemali používať na nesúvisiaci marketing ani iné komerčné účely, pokiaľ na to neexistuje samostatný právny základ a nie ste o tom primerane informovaný.",
          ],
          list: [
            "tréner alebo pracovníci organizácie zodpovední za váš tréningový profil,",
            "HeartStream ako poskytovateľ softvéru prevádzkujúci platformu pre túto organizáciu,",
            "technickí poskytovatelia služieb zapojení do hostingu, ukladania a zabezpečenia systému, ak je to potrebné.",
          ],
        },
        {
          title: "6. Ako dlho sa údaje uchovávajú",
          paragraphs: [
            "Odoslané údaje by sa mali uchovávať len tak dlho, ako je potrebné na administráciu tréningu, koučing, plnenie právnych povinností a vedenie záznamov organizácie, ktorá formulár prevádzkuje.",
            "Ak chcete poznať konkrétnu dobu uchovávania svojho profilu, kontaktujte trénera alebo organizáciu, ktorá vám poslala odkaz na formulár.",
          ],
        },
        {
          title: "7. Vaše práva",
          paragraphs: [
            "V závislosti od uplatniteľného práva a úlohy organizácie spracúvajúcej vaše údaje môžete mať právo požiadať o prístup, opravu, vymazanie, obmedzenie spracúvania, namietať proti spracúvaniu alebo odvolať súhlas.",
            "Žiadosti týkajúce sa odoslaných údajov smerujte na trénera alebo organizáciu, ktorá vám poslala tento odkaz. Táto organizácia je hlavným kontaktným miestom pre rozhodnutia o vašich údajoch.",
          ],
        },
        {
          title: "8. Bezpečnosť a sťažnosti",
          paragraphs: [
            "Platforma má používať primerané technické a organizačné opatrenia na ochranu odoslaných údajov pred neoprávneným prístupom, stratou, zneužitím alebo nezákonným zverejnením.",
            "Ak sa domnievate, že vaše údaje boli spracúvané nezákonne, môžete kontaktovať organizáciu, ktorá vám poslala odkaz, a tam, kde je to možné, podať sťažnosť príslušnému úradu na ochranu osobných údajov.",
          ],
        },
      ],
    },
    secretDialog: {
      warning:
        "Táto hodnota sa zobrazí iba raz. Skopírujte ju a uložte na bezpečné miesto. Po zatvorení dialógu k nej už nebudete mať prístup.",
      acknowledge: "Hodnotu som uložil(a) na bezpečné miesto.",
    },
  },
};
